import { useEffect, useState } from 'react';
import { Play, Loader2, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FileUploadField from './FileUploadField';
import HelpTip from './HelpTip';
import FilterToolbar, { FilterForm } from './FilterToolbar';

export default function DemographicRecon() {
  const [oracleFile, setOracleFile] = useState(null);
  const [adpFile, setAdpFile] = useState(null);
  const [mappingFile, setMappingFile] = useState(null);
  const [docsInputs, setDocsInputs] = useState(null);
  const [mappingInfo, setMappingInfo] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    apiFetch('/api/docs/inputs')
      .then(setDocsInputs)
      .catch(() => setDocsInputs(null));

    apiFetch('/api/demographic/mappings')
      .then((d) => setMappingInfo(d))
      .catch(() => setMappingInfo(null));

    apiFetch('/api/demographic/summary')
      .then((d) => {
        if (d.summary) {
          return apiFetch('/api/demographic/exceptions?limit=5000').then((ex) => {
            setResult({ summary: d.summary, exceptions: ex.rows || [] });
          });
        }
      })
      .catch(() => {});
  }, []);

  const runRecon = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    try {
      const fd = new FormData();
      if (!oracleFile && !docsInputs?.employeeMaster) {
        throw new Error('Upload Oracle employee master or save one via Reports.');
      }
      if (!adpFile) {
        throw new Error('Upload the ADP employee master report.');
      }
      if (oracleFile) fd.append('oracleEmployeeMaster', oracleFile);
      fd.append('adpEmployeeMaster', adpFile);
      if (mappingFile) fd.append('mappingFile', mappingFile);

      const base = getApiBase();
      const res = await fetch(`${base}/api/demographic/run`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Demographic reconciliation failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const summary = result?.summary;
  const rows = result?.exceptions || [];
  const needle = filter.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (category && r.category !== category) return false;
    if (!needle) return true;
    return [
      r.personNumber,
      r.gvFieldName,
      r.oracleSourceElement,
      r.description,
      r.oracleValue,
      r.adpValue,
      r.detail,
      r.category,
    ]
      .map((v) => String(v ?? '').toLowerCase())
      .some((v) => v.includes(needle));
  });

  const runLabel = summary?.runAt
    ? new Date(summary.runAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Reconciliation</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">
          Demographic reconciliation
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
          Compare Oracle HCM employee master data against the ADP employee master report using OGPI
          field mappings (IT0002 personal data and IT0105 communication). Columns are matched by
          header names — upload an optional custom mapping file to override the default pack.
        </p>
        {mappingInfo && (
          <p className="mt-2 text-xs text-slate-500">
            Default mapping: {mappingInfo.source} · {mappingInfo.fieldCount} fields · infotypes{' '}
            {mappingInfo.infotypes?.join(', ')}
          </p>
        )}
      </header>

      <ReconSection
        title="Sources & run"
        description="Oracle employee master, ADP employee master, optional custom mapping file"
        headerCenter={
          <button type="button" onClick={runRecon} disabled={running} className="btn-primary">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run demographic recon
          </button>
        }
        headerExtra={
          runLabel && (
            <p className="text-right text-xs text-slate-500">
              Last run {runLabel}
            </p>
          )
        }
      >
        <div className="grid gap-3">
          <FileUploadField
            label="Oracle employee master"
            hint="Upload to override saved employee master from Reports"
            accept=".xlsx,.xls"
            file={oracleFile}
            onChange={setOracleFile}
            savedFile={docsInputs?.employeeMaster}
            helpTerm="infotype"
            HelpTipComponent={HelpTip}
          />
          <FileUploadField
            label="ADP employee master"
            hint="Global ID / Client ID plus name, birth date, and related personal fields"
            accept=".xlsx,.xls"
            file={adpFile}
            onChange={setAdpFile}
            helpTerm="globalId"
            HelpTipComponent={HelpTip}
          />
          <FileUploadField
            label="Mapping file (optional)"
            hint="Optional file to override the default OGPI demographics mapping"
            accept=".json,application/json"
            file={mappingFile}
            onChange={setMappingFile}
            optional
          />
        </div>
        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>

      {summary && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-neutral inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Oracle {summary.personPresence?.oracleCount?.toLocaleString() ?? '—'} · ADP{' '}
              {summary.personPresence?.adpCount?.toLocaleString() ?? '—'} · both{' '}
              {summary.personPresence?.both?.toLocaleString() ?? '—'}
            </span>
            <span className="badge-success inline-flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {summary.matchedCount?.toLocaleString() ?? 0} matched fields
            </span>
            <span className="badge-warning inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {summary.exceptionCount?.toLocaleString() ?? 0} exceptions
            </span>
            {summary.fieldMismatchCount > 0 && (
              <span className="badge-warning">
                {summary.fieldMismatchCount.toLocaleString()} field mismatches
              </span>
            )}
            {(summary.onlyOracleCount > 0 || summary.onlyAdpCount > 0) && (
              <span className="badge-neutral">
                {summary.onlyOracleCount} only Oracle · {summary.onlyAdpCount} only ADP
              </span>
            )}
          </div>

          {(summary.oracleMeta?.columnLabels || summary.adpMeta?.columnLabels) && (
            <div className="card-panel p-4 text-xs text-slate-400">
              <p className="font-medium text-slate-300">Columns detected</p>
              {summary.oracleMeta?.columnLabels?.personNumber && (
                <p className="mt-1">
                  Oracle person:{' '}
                  <span className="font-mono text-slate-300">
                    {summary.oracleMeta.columnLabels.personNumber}
                  </span>
                </p>
              )}
              {summary.adpMeta?.columnLabels?.personNumber && (
                <p>
                  ADP person:{' '}
                  <span className="font-mono text-slate-300">
                    {summary.adpMeta.columnLabels.personNumber}
                  </span>
                </p>
              )}
            </div>
          )}

          <div className="card-panel overflow-hidden">
            <div className="section-header flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-100">Exceptions</h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-200"
                >
                  <option value="">All categories</option>
                  <option value="field_mismatch">Field mismatch</option>
                  <option value="only_oracle">Only Oracle</option>
                  <option value="only_adp">Only ADP</option>
                </select>
                <FilterForm onApply={() => setFilter(filterDraft)}>
                  <FilterToolbar
                    onClear={() => {
                      setFilterDraft('');
                      setFilter('');
                    }}
                    hasActiveFilters={!!filter.trim()}
                  >
                    <input
                      type="search"
                      placeholder="Person, field, value…"
                      value={filterDraft}
                      onChange={(e) => setFilterDraft(e.target.value)}
                      className="filter-input min-w-[12rem] text-sm"
                    />
                  </FilterToolbar>
                </FilterForm>
              </div>
            </div>
            <div className="table-scroll">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Category</th>
                    <th>GV field</th>
                    <th>Oracle element</th>
                    <th>Oracle value</th>
                    <th>ADP value</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-slate-500">
                        No exceptions match the filter.
                      </td>
                    </tr>
                  ) : (
                    visible.slice(0, 500).map((r, i) => (
                      <tr key={`${r.personNumber}-${r.gvFieldName}-${i}`}>
                        <td className="font-mono">{r.personNumber}</td>
                        <td>{r.category}</td>
                        <td className="font-mono">{r.gvFieldName || '—'}</td>
                        <td>{r.oracleSourceElement || '—'}</td>
                        <td className="max-w-[10rem] truncate" title={r.oracleValue}>
                          {r.oracleValue || '—'}
                        </td>
                        <td className="max-w-[10rem] truncate" title={r.adpValue}>
                          {r.adpValue || '—'}
                        </td>
                        <td className="max-w-xs truncate text-slate-400" title={r.detail}>
                          {r.detail || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {visible.length > 500 && (
              <p className="px-4 py-2 text-xs text-slate-500">
                Showing first 500 of {visible.length.toLocaleString()} rows.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
